import type { InitialSemanticState, PatchOperation, Projection } from "./model.ts";

const API_BASE = (import.meta.env?.VITE_UDF_API_BASE as string | undefined) ?? "/v0";
const SESSION_REQUIRED_EVENT = "bep-session-required";
let browserCSRFToken: string | null = null;

export interface BrowserSession {
  session_id: string;
  expires_at: string;
  csrf_token: string;
  tenant_name: string;
  client_name: string;
}

export type BrowserSessionState =
  | { mode: "disabled" }
  | { mode: "required" }
  | { mode: "authenticated"; session: BrowserSession };

export async function restoreBrowserSession(): Promise<BrowserSessionState> {
  const response = await fetch(`${API_BASE}/browser-session`, { credentials: "same-origin" });
  if (response.status === 404) return { mode: "disabled" };
  if (response.status === 401) {
    browserCSRFToken = null;
    return { mode: "required" };
  }
  if (!response.ok) throw await responseError(response);
  const session = (await response.json()) as BrowserSession;
  browserCSRFToken = session.csrf_token;
  return { mode: "authenticated", session };
}

export async function createBrowserSession(apiKey: string): Promise<BrowserSession> {
  const response = await fetch(`${API_BASE}/browser-sessions`, {
    method: "POST",
    credentials: "same-origin",
    headers: { Authorization: `Bearer ${apiKey}`, Accept: "application/json" },
  });
  if (!response.ok) throw await responseError(response);
  const session = (await response.json()) as BrowserSession;
  browserCSRFToken = session.csrf_token;
  return session;
}

export async function deleteBrowserSession(): Promise<void> {
  const headers = new Headers({ Accept: "application/json" });
  if (browserCSRFToken) headers.set("X-BEP-CSRF", browserCSRFToken);
  const response = await fetch(`${API_BASE}/browser-session`, {
    method: "DELETE",
    credentials: "same-origin",
    headers,
  });
  if (!response.ok && response.status !== 401) throw await responseError(response);
  browserCSRFToken = null;
}

export interface VersionRecord {
  id: string;
  document_id: string;
  branch_id: string;
  sequence: number;
  ruleset_version: string;
  semantic_root: string;
  created_at: string;
}

export interface SessionRecord {
  documentId: string;
  branchId: string;
  versionId: string;
}

export interface ValidationIssue {
  code: string;
  severity: "info" | "warning" | "error";
  stage: string;
  node_ref?: string;
  path?: string;
  message: string;
}

export interface ValidationReport {
  valid: boolean;
  runtime_ruleset: string;
  domain_ruleset: string;
  issues: ValidationIssue[];
}

export type RenderProfile = "swiss.visual.a4" | "swiss.visual.letter" | "resume.ats.pdf" | "resume.ats.docx";
export type RenderStatus = "queued" | "running" | "cancellation_requested" | "preflight_failed" | "completed" | "failed" | "cancelled";

export interface RenderJob {
  id: string;
  document_id: string;
  source_version_id: string;
  projection_id: string;
  projection_digest: string;
  kind: "render.pdf" | "render.docx";
  profile: RenderProfile;
  profile_version: string;
  format: "pdf" | "docx";
  intent: "draft" | "final";
  style_package: string;
  status: RenderStatus;
  attempt: number;
  created_at: string;
  updated_at: string;
  result: unknown;
  error: unknown;
}

export interface Artifact {
  id: string;
  job_id: string;
  document_id: string;
  source_version_id: string;
  projection_id: string;
  profile: RenderProfile;
  profile_version: string;
  format: "pdf" | "docx";
  lifecycle: "draft" | "final";
  media_type: string;
  size_bytes: number;
  digest: string;
  safe_filename: string;
  created_at: string;
}

export interface ArtifactDetail {
  artifact: Artifact;
  manifest: unknown;
  manifest_digest: string;
}

export type AdmissionState = "quarantine" | "scanning" | "accepted" | "rejected";

export interface IngestionAdmission {
  binding_id: string;
  document_id: string;
  state: AdmissionState;
  scanner_name: string;
  scanner_version: string;
  definitions_version?: string;
  verdict?: "clean" | "malware_detected";
  error_code?: "MALWARE_SCANNER_UNAVAILABLE" | "MALWARE_SCAN_RESULT_INVALID" | "MALWARE_DETECTED";
  attempt: number;
  started_at: string;
  completed_at?: string;
  updated_at: string;
  retryable: boolean;
}

export interface DocumentIngestionStatus {
  document_id: string;
  source: { binding_id: string; sync_state: string } | null;
  admissions: IngestionAdmission[];
  latest_import: {
    job_id: string;
    status: "pending" | "running" | "committed" | "failed" | "quarantined";
    attempt: number;
    error_code?: string;
    committed_version_id?: string;
    updated_at: string;
  } | null;
}

export interface RetryAdmissionResult {
  admission: IngestionAdmission;
  replayed: boolean;
}
export interface DocumentImportRecord {
  document_id: string;
  binding_id: string;
  version_id: string;
  source_version: string;
  filename: string;
  media_type: string;
  size_bytes: number;
  digest: string;
  status: "pending";
  replayed: boolean;
}

export const MAX_DOCUMENT_IMPORT_BYTES = 25 * 1024 * 1024;


export class APIError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: string;

  constructor(status: number, code: string, title: string, detail: string) {
    super(title);
    this.name = "APIError";
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

export async function createResume(initial: InitialSemanticState): Promise<SessionRecord> {
  const response = await request<{
    document: { id: string };
    branch: { id: string };
    version: VersionRecord;
  }>("/documents", {
    method: "POST",
    idempotencyKey: operationKey("resume-create"),
    body: { domain: { name: "resume", version: "0.1.0" }, initial },
  });
  return { documentId: response.document.id, branchId: response.branch.id, versionId: response.version.id };
}

export async function loadLatestSession(documentId: string): Promise<SessionRecord> {
  const [document, versions] = await Promise.all([
    request<{ id: string; default_branch_id: string }>(`/documents/${documentId}`),
    request<{ items: VersionRecord[] }>(`/documents/${documentId}/versions?limit=1`),
  ]);
  const latest = versions.items[0];
  if (!latest) throw new Error("This resume has no stored version.");
  return { documentId, branchId: document.default_branch_id, versionId: latest.id };
}

export async function createProjection(session: SessionRecord): Promise<Projection> {
  const response = await request<{ projection: Projection }>(`/documents/${session.documentId}/projections`, {
    method: "POST",
    idempotencyKey: operationKey("resume-projection"),
    body: { version_id: session.versionId, purpose: "edit", profile: "zeitpoint.resume.editor.v0" },
  });
  return response.projection;
}

export async function createRenderProjection(session: SessionRecord, signal?: AbortSignal): Promise<Projection> {
  const response = await request<{ projection: Projection }>(`/documents/${session.documentId}/projections`, {
    method: "POST",
    idempotencyKey: operationKey("resume-render-projection"),
    body: { version_id: session.versionId, purpose: "render", profile: "zeitpoint.resume.render.v0" },
    signal,
  });
  return response.projection;
}

export async function createRender(session: SessionRecord, projectionId: string, profile: RenderProfile, signal?: AbortSignal): Promise<RenderJob> {
  const format = profile === "resume.ats.docx" ? "docx" : "pdf";
  const response = await request<{ job: RenderJob }>(`/documents/${session.documentId}/renders`, {
    method: "POST",
    idempotencyKey: operationKey("resume-render"),
    body: { version_id: session.versionId, projection_id: projectionId, profile, format, intent: "final" },
    signal,
  });
  return response.job;
}

export async function getRenderJob(jobId: string, signal?: AbortSignal): Promise<RenderJob> {
  const response = await request<{ job: RenderJob }>(`/jobs/${jobId}`, { signal });
  return response.job;
}

export async function getArtifact(artifactId: string, signal?: AbortSignal): Promise<ArtifactDetail> {
  return request<ArtifactDetail>(`/artifacts/${artifactId}`, { signal });
}

export async function downloadArtifactContent(artifact: Artifact, signal?: AbortSignal): Promise<Blob> {
  const response = await requestRaw(`/artifacts/${artifact.id}/content`, { signal, accept: artifact.media_type });
  return response.blob();
}

export function artifactIDFromJob(job: RenderJob): string | null {
  if (job.status !== "completed" || typeof job.result !== "object" || job.result === null) return null;
  const value = (job.result as { artifact_id?: unknown }).artifact_id;
  return typeof value === "string" && value.startsWith("artifact_") ? value : null;
}

export async function commitResume(session: SessionRecord, operations: PatchOperation[]): Promise<SessionRecord> {
  const response = await request<{ version: VersionRecord }>(`/documents/${session.documentId}/commits`, {
    method: "POST",
    idempotencyKey: operationKey("resume-save"),
    body: {
      branch_id: session.branchId,
      base_version: session.versionId,
      patch: { profile: "udf-public-patch-0.1", operations },
      message: "ZEITPOINT resume edit",
    },
  });
  return { ...session, versionId: response.version.id };
}

export async function validateVersion(session: SessionRecord): Promise<ValidationReport> {
  const response = await request<{ report: ValidationReport }>(`/documents/${session.documentId}/validations`, {
    method: "POST",
    body: { version_id: session.versionId },
  });
  return response.report;
}

export async function listVersions(documentId: string): Promise<VersionRecord[]> {
  const response = await request<{ items: VersionRecord[] }>(`/documents/${documentId}/versions?limit=50`);
  return response.items;
}

export async function getDocumentIngestion(documentId: string, signal?: AbortSignal): Promise<DocumentIngestionStatus> {
  return request<DocumentIngestionStatus>(`/documents/${documentId}/ingestion`, { signal });
}

export async function retryDocumentIngestion(documentId: string, signal?: AbortSignal): Promise<RetryAdmissionResult> {
  return request<RetryAdmissionResult>(`/documents/${documentId}/ingestion/retry`, {
    method: "POST",
    idempotencyKey: operationKey("ingestion-retry"),
    body: {},
    signal,
  });
}

export async function createDocumentImport(file: File, signal?: AbortSignal): Promise<DocumentImportRecord> {
  const mediaType = file.type || "application/octet-stream";
  const encodedFilename = encodeURIComponent(file.name).replace(/[!'()*]/g, (character) => `%${character.charCodeAt(0).toString(16).toUpperCase()}`);
  return request<DocumentImportRecord>("/document-imports", {
    method: "POST",
    idempotencyKey: operationKey("document-import"),
    rawBody: file,
    contentType: mediaType,
    contentDisposition: `attachment; filename*=UTF-8''${encodedFilename}`,
    signal,
  });
}

interface RequestOptions {
  method?: "GET" | "POST" | "DELETE";
  body?: unknown;
  idempotencyKey?: string;
  signal?: AbortSignal | undefined;
  accept?: string;
  rawBody?: BodyInit;
  contentType?: string;
  contentDisposition?: string;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await requestRaw(path, options);
  return (await response.json()) as T;
}

async function requestRaw(path: string, options: RequestOptions = {}): Promise<Response> {
  const headers = new Headers({ Accept: options.accept ?? "application/json" });
  if (options.body !== undefined && options.rawBody !== undefined) throw new Error("Request cannot contain both JSON and raw bodies.");
  if (options.body !== undefined) headers.set("Content-Type", "application/json");
  if (options.rawBody !== undefined) headers.set("Content-Type", options.contentType ?? "application/octet-stream");
  if (options.contentDisposition) headers.set("Content-Disposition", options.contentDisposition);
  if (options.idempotencyKey) headers.set("Idempotency-Key", options.idempotencyKey);
  const method = options.method ?? "GET";
  if (method !== "GET" && browserCSRFToken) headers.set("X-BEP-CSRF", browserCSRFToken);
  const init: RequestInit = { method, headers, credentials: "same-origin" };
  if (options.signal) init.signal = options.signal;
  if (options.body !== undefined) init.body = JSON.stringify(options.body);
  if (options.rawBody !== undefined) init.body = options.rawBody;
  const response = await fetch(`${API_BASE}${path}`, init);
  if (!response.ok) {
    if (response.status === 401) {
      browserCSRFToken = null;
      if (typeof window !== "undefined") window.dispatchEvent(new Event(SESSION_REQUIRED_EVENT));
    }
    throw await responseError(response);
  }
  return response;
}

async function responseError(response: Response): Promise<APIError> {
  let problem: { code?: string; title?: string; detail?: string } = {};
  try {
    problem = (await response.json()) as typeof problem;
  } catch {
    problem = {};
  }
  return new APIError(response.status, problem.code ?? "HTTP_ERROR", problem.title ?? "Request failed", problem.detail ?? `The server returned ${response.status}.`);
}

export function browserSessionRequiredEvent(): string {
  return SESSION_REQUIRED_EVENT;
}

function operationKey(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}
